import {v4 as uuid} from 'uuid';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useViewPresentedEvent from '@contentlab/hooks/analytics/use-view-presented-event';
import {CONTENT_LAB_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import {contentLabActions} from '../../stores/contentLabStore';

export default function usePushNotifications() {
  const {viewPresented} = useViewPresentedEvent();
  const {uiInteraction} = useUiInteractionEvent();
  async function requestPermission(
    interactionLocation: (typeof CONTENT_LAB_VIEW_NAMES)[keyof typeof CONTENT_LAB_VIEW_NAMES]
  ) {
    if (
      typeof window === 'undefined' ||
      typeof window.Notification === 'undefined'
    ) {
      return;
    }
    const currentPermission = window.Notification.permission;
    const newPermission = await window.Notification.requestPermission();
    if (newPermission !== currentPermission) {
      const presentationId = uuid();
      viewPresented({
        view_name: 'Notification Permission Popup',
        view_type: 'modal',
        view_location: interactionLocation,
        presentation_id: presentationId,
        source: 'notification_permission_popup',
      });
      uiInteraction({
        item_name: 'notification_permission_popup',
        interaction_location: interactionLocation,
        presentation_id: presentationId,
        interaction_details: newPermission,
      });
      if (newPermission === 'granted') {
        contentLabActions.addAlert({
          title: translate('Notifications Enabled'),
          description: translate(
            "You'll get updates in the app or by email — even if you leave this page."
          ),
        });
      }
    }
  }

  async function sendNotification(
    interactionLocation: keyof typeof CONTENT_LAB_VIEW_NAMES,
    title: string,
    body: string,
    onClick?: () => void
  ) {
    if (
      typeof window === 'undefined' ||
      typeof window.Notification === 'undefined'
    ) {
      return;
    }
    await requestPermission(CONTENT_LAB_VIEW_NAMES[interactionLocation]);
    if (window.Notification.permission === 'granted') {
      const notification = new window.Notification(title, {
        body,
        icon: 'https://storage.googleapis.com/brands-public-assets/content-lab/logo-content-lab-purple.svg',
      });
      notification.onclick = () => {
        onClick?.();
      };
    }
  }

  return {requestPermission, sendNotification};
}
